import { useEffect, useMemo, useRef, useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/ui/truncated-text";
import { SearchFilterBar, type FilterColumn } from "@/components/shared/SearchFilterBar";
import {
  selectTypeaheadSuggestions,
  type BankPickerListFilter,
  type BankPickerRow,
} from "./select-bank-picker-rows";

/** `prototype-extracted.md § 4.6`: blur closes the typeahead after this delay, so a click on a
 *  suggestion row (which blurs the input first) still lands before the popover disappears. */
const BLUR_CLOSE_DELAY_MS = 120;

const LIST_FILTER_OPTIONS: FilterColumn["options"] = [
  { value: "master", label: "Master list" },
  { value: "bank", label: "Keyword bank" },
];

export interface KeywordBankPickerSearchProps {
  query: string;
  onQueryChange: (value: string) => void;
  list: Set<BankPickerListFilter>;
  onListChange: (list: Set<BankPickerListFilter>) => void;
  themeFilter: Set<string>;
  onThemeFilterChange: (themes: Set<string>) => void;
  /** Every theme name present in the bank, alphabetical (`sources.md § (c)`). */
  themeOptions: string[];
  /** The full, un-capped, un-filtered bank rows -- the typeahead searches across all of them. */
  rows: BankPickerRow[];
  /** Sets the search query to the picked suggestion's keyword; never the row-apply handler. */
  onPickSuggestion: (row: BankPickerRow) => void;
  /** The measured height of this bar, fed to the table as its `stickyHeaderOffset`. */
  onHeightChange: (height: number) => void;
}

/**
 * The Keywords tab's search bar (`SearchFilterBar` / `FilterDropdown`, used unmodified) plus the
 * typeahead popover, sticky above the table. `prototype-extracted.md § 4.6`,
 * `sources.md § (c)` — *Typeahead*.
 */
export function KeywordBankPickerSearch({
  query,
  onQueryChange,
  list,
  onListChange,
  themeFilter,
  onThemeFilterChange,
  themeOptions,
  rows,
  onPickSuggestion,
  onHeightChange,
}: KeywordBankPickerSearchProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [manuallyClosed, setManuallyClosed] = useState(false);

  // Measures this bar's own height so the host can pass it straight through as the table's
  // `stickyHeaderOffset` (sources.md § (c): "the table header pins beneath it").
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => onHeightChange(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FilterDropdown manages its own Popover open state internally and exposes no callback for it
  // (it is used unmodified, conventions/09-design-conformance.md), so the only way to learn
  // whether it is open from outside is the `data-state` Radix already writes onto its trigger.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const compute = () => setIsFilterOpen(!!el.querySelector('[data-state="open"]'));
    compute();
    const observer = new MutationObserver(compute);
    observer.observe(el, { attributes: true, attributeFilter: ["data-state"], subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    [],
  );

  const suggestions = useMemo(() => selectTypeaheadSuggestions(rows, query), [rows, query]);
  const typeaheadOpen = focused && !manuallyClosed && query.trim() !== "";

  const filterColumns: FilterColumn[] = useMemo(
    () => [
      { id: "list", label: "List", options: LIST_FILTER_OPTIONS },
      {
        id: "theme",
        label: "Theme",
        options: themeOptions.map((name) => ({ value: name, label: name })),
      },
    ],
    [themeOptions],
  );

  const activeFilters = useMemo(
    () => ({ list: Array.from(list), theme: Array.from(themeFilter) }),
    [list, themeFilter],
  );

  const handleFiltersChange = (next: Record<string, string[]>) => {
    onListChange(new Set((next.list ?? []) as BankPickerListFilter[]));
    onThemeFilterChange(new Set(next.theme ?? []));
  };

  const handleQueryChange = (value: string) => {
    setManuallyClosed(false);
    onQueryChange(value);
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setFocused(true);
    setManuallyClosed(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    blurTimeoutRef.current = setTimeout(() => setFocused(false), BLUR_CLOSE_DELAY_MS);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (e.key === "Enter" || e.key === "Escape") setManuallyClosed(true);
  };

  const handlePick = (row: BankPickerRow) => {
    setManuallyClosed(true);
    onPickSuggestion(row);
  };

  return (
    <div
      ref={wrapperRef}
      data-testid="keyword-bank-picker-search-bar"
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cn(
        // AUT-454: `sticky` alone never pins without a `top` — the bar scrolled away with the
        // rows, leaving the table's sticky header floating `searchBarHeight` below the scroll
        // container's top with nothing behind it. The scroll ancestor here (VariantBPane's
        // `overflow-y-auto` pane) starts right at this bar, so 0 is the correct offset.
        "sticky top-0 flex items-center gap-3 flex-wrap bg-card py-2",
        "after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-card",
        isFilterOpen ? "z-[60]" : "z-40",
      )}
    >
      <SearchFilterBar
        search={query}
        onSearchChange={handleQueryChange}
        placeholder="Search keywords…"
        filterColumns={filterColumns}
        activeFilters={activeFilters}
        onFiltersChange={handleFiltersChange}
      >
        {typeaheadOpen && (
          <div
            data-testid="keyword-bank-picker-typeahead"
            className="absolute z-50 left-0 top-full mt-1 w-80 rounded-lg border border-border bg-card shadow-md overflow-hidden"
          >
            {suggestions.map((row) => (
              <button
                key={row.keywordId}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                onClick={() => handlePick(row)}
              >
                {row.isMaster ? (
                  <Star
                    className="h-4 w-4 shrink-0 fill-current text-amber-400"
                    aria-label="In master list"
                  />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
                <TruncatedText className="min-w-0 flex-1">{row.keyword}</TruncatedText>
                {row.theme && (
                  <span className="ml-auto flex items-center gap-1.5 shrink-0">
                    <Badge variant="secondary" className="text-[11px] px-2 py-0">
                      {row.theme}
                    </Badge>
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </SearchFilterBar>
    </div>
  );
}
